import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const CreditManagement = ({ user, userId, onPurchaseCredits }) => {
  const creditsRemaining = user?.creditsRemaining ?? 0;
  const isLowCredits = creditsRemaining <= 2;
  const hasNoCredits = creditsRemaining === 0;

  const [usageStats, setUsageStats] = useState(null);
  const [recentUsage, setRecentUsage] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUsageData = async () => {
      if (!userId) { setIsLoading(false); return; }
      try {
        // Fetch all analysis history for stats
        const { data: historyData } = await supabase?.from('analysis_history')?.select('id, photo_count, credits_used, created_at')?.eq('user_id', userId)?.order('created_at', { ascending: false });

        // Fetch total credits purchased
        const { data: purchasesData } = await supabase?.from('purchases')?.select('credits_purchased, created_at')?.eq('user_id', userId)?.order('created_at', { ascending: false });

        const totalPurchased = purchasesData?.reduce((sum, p) => sum + (p?.credits_purchased || 0), 0) || 0;
        const totalUsed = historyData?.reduce((sum, h) => sum + (h?.credits_used || 0), 0) || 0;
        const lastPurchase = purchasesData?.[0]?.created_at || null;

        setUsageStats({
          totalPurchased,
          totalUsed,
          remaining: creditsRemaining,
          averagePerAnalysis: 1,
          lastPurchaseDate: lastPurchase
        });

        // Map recent 5 analyses to usage items
        const recent = (historyData || [])?.slice(0, 5)?.map(h => ({
          date: h?.created_at,
          credits: h?.credits_used || 1,
          description: `Photo ranking analysis (${h?.photo_count || 0} photos)`
        }));
        setRecentUsage(recent);
      } catch (err) {
        console.error('Failed to load credit usage:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsageData();
  }, [userId, creditsRemaining]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Current Balance */}
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="Zap" size={20} color="#9333EA" />
          Credit Balance
        </h2>

        <div className="bg-gradient-to-r from-purple-600 to-cyan-500 rounded-xl p-5 sm:p-6 text-white">
          <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4">
            <div className="text-center sm:text-left flex-1">
              <p className="text-sm opacity-90 mb-1">Available Credits</p>
              <p className="text-4xl sm:text-5xl font-bold">{creditsRemaining}</p>
              <p className="text-sm opacity-90 mt-2">
                {hasNoCredits
                  ? 'Purchase credits to continue analyzing photos'
                  : isLowCredits
                  ? 'Running low! Consider purchasing more credits' :'You have enough credits for multiple analyses'}
              </p>
            </div>
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Icon name="Zap" size={36} color="#FFFFFF" />
            </div>
          </div>

          <div className="mt-5 sm:mt-6">
            <Button
              onClick={onPurchaseCredits}
              variant="outline"
              className="bg-white text-purple-600 hover:bg-gray-100 border-0 w-full sm:w-auto min-h-[44px]"
              iconName="ShoppingCart"
            >
              Purchase More Credits
            </Button>
          </div>
        </div>
      </div>
      {/* Usage Statistics */}
      <div className="border-t pt-6 sm:pt-8">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="BarChart3" size={20} color="#9333EA" />
          Usage Statistics
        </h2>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 animate-pulse">
            {[...Array(4)]?.map((_, i) => <div key={i} className="h-24 bg-gray-100 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ShoppingBag" size={16} color="#9333EA" />
                <p className="text-sm font-medium text-gray-700">Total Purchased</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{usageStats?.totalPurchased ?? 0}</p>
              <p className="text-xs text-gray-600 mt-1">All-time credit purchases</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={16} color="#06B6D4" />
                <p className="text-sm font-medium text-gray-700">Total Used</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{usageStats?.totalUsed ?? 0}</p>
              <p className="text-xs text-gray-600 mt-1">Credits spent on analyses</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Activity" size={16} color="#10B981" />
                <p className="text-sm font-medium text-gray-700">Average Per Analysis</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{usageStats?.averagePerAnalysis ?? 1}</p>
              <p className="text-xs text-gray-600 mt-1">Credits per photo ranking</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Calendar" size={16} color="#F59E0B" />
                <p className="text-sm font-medium text-gray-700">Last Purchase</p>
              </div>
              <p className="text-base sm:text-lg font-bold text-gray-900">
                {usageStats?.lastPurchaseDate ? formatDate(usageStats?.lastPurchaseDate) : 'No purchases yet'}
              </p>
              <p className="text-xs text-gray-600 mt-1">Most recent credit purchase</p>
            </div>
          </div>
        )}
      </div>
      {/* Recent Usage */}
      <div className="border-t pt-6 sm:pt-8">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="History" size={20} color="#9333EA" />
          Recent Usage
        </h2>

        {isLoading ? (
          <div className="space-y-2 animate-pulse">
            {[...Array(5)]?.map((_, i) => <div key={i} className="h-14 bg-gray-100 rounded-lg" />)}
          </div>
        ) : recentUsage?.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No analyses yet. Upload photos to get started!</p>
        ) : (
          <div className="space-y-2">
            {recentUsage?.map((usage, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors gap-3"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="Image" size={16} color="#9333EA" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{usage?.description}</p>
                    <p className="text-xs text-gray-600">{formatDate(usage?.date)}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-gray-900">-{usage?.credits}</p>
                  <p className="text-xs text-gray-600">credit</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Credit Packages Info */}
      <div className="border-t pt-6 sm:pt-8">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="Package" size={20} color="#9333EA" />
          Available Packages
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg p-4 border-2 border-gray-200 hover:border-purple-300 transition-colors">
            <p className="text-sm font-medium text-gray-700 mb-1">Starter Pack</p>
            <p className="text-2xl font-bold text-gray-900 mb-1">10 Credits</p>
            <p className="text-lg text-purple-600 font-semibold mb-2">$9.99</p>
            <p className="text-xs text-gray-600">$1.00 per credit</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-cyan-50 rounded-lg p-4 border-2 border-purple-300">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium text-purple-900">Popular Pack</p>
              <span className="px-2 py-0.5 bg-purple-600 text-white text-xs font-bold rounded-full">
                POPULAR
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900 mb-1">25 Credits</p>
            <p className="text-lg text-purple-600 font-semibold mb-2">$19.99</p>
            <p className="text-xs text-gray-600">$0.80 per credit • Save 20%</p>
          </div>

          <div className="bg-white rounded-lg p-4 border-2 border-gray-200 hover:border-purple-300 transition-colors">
            <p className="text-sm font-medium text-gray-700 mb-1">Pro Pack</p>
            <p className="text-2xl font-bold text-gray-900 mb-1">50 Credits</p>
            <p className="text-lg text-purple-600 font-semibold mb-2">$34.99</p>
            <p className="text-xs text-gray-600">$0.70 per credit • Save 30%</p>
          </div>
        </div>

        <div className="mt-4 text-center">
          <Button
            onClick={onPurchaseCredits}
            iconName="ShoppingCart"
            size="lg"
            className="w-full sm:w-auto min-h-[44px]"
          >
            View All Packages
          </Button>
        </div>
      </div>
      {/* Info Box */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex gap-3">
        <Icon name="Info" size={20} color="#9333EA" className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-purple-900">About Credits</p>
          <p className="text-sm text-purple-700 mt-1">
            Each photo ranking analysis uses 1 credit, regardless of the number of photos (1-9). Credits never expire and can be used anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreditManagement;